import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom';
import MuiAppBar from '@mui/material/AppBar';
import MenuIcon from '@mui/icons-material/Menu';
import stringAvatar from '../../lib/stringAvatar';
import LogoutIcon from '@mui/icons-material/Logout';
import { Avatar, Badge, Box, Divider, Fab, IconButton, Menu, styled, Toolbar, Typography, useTheme } from '@mui/material'
import IconBackground from '../../Component/Common/IconBackground';
import Assets from '../../Component/Common/Assets';
import Notifications from '../../Component/Notifications/Notifications';
import CustomDrawer from '../../Component/Common/CustomDrawer';
import { useAppSelector } from '../../Redux/app/hooks';
import localStoreUtil from '../../ApiSetUp/localstore.util';
import { useDispatch } from 'react-redux';
import usePageLoader from '../../Redux/hooks/usePageLoader';
import useSnackBar from '../../Redux/hooks/useSnackBar';
import ErrorHandler from '../../lib/errorHandler';
import { get_all_notifications } from '../../Redux/Actions/AuthUser';

const drawerWidth = 320;

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
    backgroundColor: theme.palette.bgWhite.main,
    boxShadow: "none",
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    borderBottom: "1px solid #0404041A",
    variants: [
        {
            props: ({ open }) => open,
            style: {
                width: `calc(100% - ${drawerWidth}px)`,
                marginLeft: `${drawerWidth}px`,
                transition: theme.transitions.create(['margin', 'width'], {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
        },
    ],
}));


const Header = ({ open, handleDrawerOpen, logOut, filteredTabs }) => {
    // =============== Hooks ===============
    const theme = useTheme();
    const dispatch = useDispatch();
    const location = useLocation();
    const setFullPageLoader = usePageLoader();
    const { setSnackBar } = useSnackBar();

    const loggedInUserDetails = localStoreUtil?.get_data("User_Data");
    const userData = loggedInUserDetails ? JSON.parse(loggedInUserDetails) : null;
    const findActiveTab = (tabs, path) => {
        for (const tab of tabs) {
            if (tab.path === path) return tab.name;
            if (path === "/purchase-order/po-documents") {
                return "Purchase Order Documents";
            }
            if (tab.subData) {
                const found = findActiveTab(tab.subData, path);
                if (found) return found;
            }
        }
        return "";
    };
    const activeTabName = findActiveTab(filteredTabs, location.pathname);

    // =============== States ===============
    const [anchorEl, setAnchorEl] = useState(null);
    const [isOpenNotification, setIsOpenNotification] = useState(false);
    const [notificationData, setNotificationData] = useState({});

    // =============== Handler ===============
    const openMenu = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };


    // ============= get Inventories API =============
    const _get_all_notifications = async (p) => {
        setFullPageLoader(true);
        let response;
        let body = {
            "limit": 10,
            "page": 1,
            "date": null,
        }
        response = await dispatch(get_all_notifications(body));
        let error = await ErrorHandler(response, setSnackBar);
        if (error) {
            if (response?.payload?.data) {
                setNotificationData(response?.payload?.data)
            }
            setFullPageLoader(false);
        }
        setFullPageLoader(false);
    };

    useEffect(() => {
        _get_all_notifications();
    }, [isOpenNotification])

    return (
        <AppBar position="fixed" open={open}>
            <Toolbar sx={{ display: "flex", justifyContent: "space-between", minHeight: "84px !important", "&.MuiToolbar-root": { paddingLeft: open ? "25px" : "90px" } }}>
                <Typography fontSize={"22px"} fontFamily={"Satoshi-Bold"} color={theme.palette.primary.main}>
                    {activeTabName}
                </Typography>
                <Box display={'flex'} alignItems={'center'} gap={1}>
                    <Badge badgeContent={notificationData?.count || 0} sx={{ "& .MuiBadge-badge": { right: "4px", top: "10px", zIndex: 9999, color: "#FFF" } }} color="primary">
                        <Fab onClick={() => setIsOpenNotification(true)} sx={{ boxShadow: "none", backgroundColor: "#F5F9FB", height: "48px", width: "48px" }}>
                            <Assets absolutePath={true} src={`/assets/img/Notification.svg`} />
                        </Fab>
                    </Badge>
                    {userData?.profilePicture ? (
                        <Avatar src={userData?.profilePicture} style={{ height: "50px", width: "50px" }} onClick={handleClick} />
                    ) : (
                        userData?.name ? (
                            <Avatar {...stringAvatar(userData?.name)} style={{ height: "50px", width: "50px" }} onClick={handleClick} />
                        ) : (
                            <Avatar style={{ height: "50px", width: "50px" }} onClick={handleClick} />
                        )
                    )}
                </Box>
            </Toolbar>
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={openMenu}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            minWidth: "200px",
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                            "& .MuiMenu-list": {
                                padding: "0px"
                            },
                            '&::before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box p={2} display={"flex"} gap={1} alignItems={"start"}>
                    {userData?.profilePicture ? (
                        <Avatar src={userData?.profilePicture} style={{ height: "50px", width: "50px" }} />
                    ) : (
                        userData?.name ? (
                            <Avatar {...stringAvatar(userData?.name)} style={{ height: "50px", width: "50px" }} />
                        ) : (
                            <Avatar style={{ height: "50px", width: "50px" }} />
                        )
                    )}
                    <Box >
                        <Typography fontSize={"18px"} fontWeight={700}>{userData?.name || "Nikunj Makwana"}</Typography>
                        <Typography fontSize={"12px"}>{userData?.email || "test123@gmail.com"}</Typography>
                    </Box>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="start" alignItems="center" gap={1} py={1} px={2} bgcolor="#0000000a"
                    sx={{ '&:hover': { bgcolor: '#00000014', cursor: 'pointer' } }}
                    onClick={(e) => logOut()}
                >
                    <LogoutIcon fontSize="small" />
                    <Typography fontSize="16px">Logout</Typography>
                </Box>

            </Menu>
            <CustomDrawer
                isDrawerOpen={isOpenNotification}
                handleClose={() => setIsOpenNotification(false)}
                title={`Notification`}
            >
                <Notifications notificationData={notificationData} setIsOpenNotification={setIsOpenNotification} />
            </CustomDrawer>
        </AppBar>
    )
}

export default Header